import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import dynamic from 'next/dynamic';
import jsCookie from 'js-cookie';
import { useTranslation } from 'next-i18next';

// COMPONENTS
import Layout from '@source/components/Layout';

// HOOKS
// import useRegionConfig from '@source/hooks/useRegionConfig';

// TYPES
import type { NextPage } from 'next';
import { IRegionProps, TRegionKey } from '@source/interface';

import HeadMeta from '@source/pages/Home/components/HeadMeta';
import {
  STEPS_TO_SELL_CAR_CONTENT,
  STEPS_TO_SELL_CAR_CONTENT_V2,
  UPS_CONTENT,
  WHY_CHOOSE_CARRO_CONTENT,
} from '@source/pages/SellCar/constants';
import CUSTOMER_REVIEW_CONTENT from '@source/components/CustomerReview/content';
import { CUSTOMER_REVIEW_CONTENT_V2 } from './constants';
import { CARRO_MOBILE_APP_KEY } from '@source/constants/common';

// First load Sell Banner
import SellBanner from '@source/pages/SellCar/components/Banner';
import InspectionFeeWaive from '@source/pages/SellCar/components/InspectionFeeWaive';
import FeaturedIn from '@source/components/FeaturedIn';
import useWindowResize from '@source/hooks/useWindowResize';
import useUTM from '@source/hooks/useUTM';
import { IInspectionCentre, ILocationCentreOption } from './components/InspectionCentres/configs/types';
import WhyChooseCarro from './components/WhyChooseCarro';
import StyledContainer from '@design-system/styled/Container';
import TrustReason from './components/TrustReason';
import VisitUs from './components/VisitUs';
import { VISIT_US_CONTENT } from './components/VisitUs/constants';

const Stats = dynamic(() => import('@source/pages/SellCar/components/Stats'));
// const FeaturedIn = dynamic(() => import('@source/components/FeaturedIn'));
const AskFaq = dynamic(() => import('@source/pages/SellCar/components/AskFaq'));
const StepsToSellCar = dynamic(() => import('@source/pages/SellCar/components/StepsToSellCar'));
const StepsToSellCarV2 = dynamic(() => import('@source/pages/SellCar/components/StepsToSellCar/v2'));
const CustomerReview = dynamic(() => import('@source/components/CustomerReview/CustomerReview'));
const CustomerReviewV2 = dynamic(() => import('@source/components/CustomerReviewV2'));
const InspectionCentres = dynamic(() => import('@source/pages/SellCar/components/InspectionCentres'), {
  ssr: false,
});
// const SellBanner = dynamic(
//   () => import('@source/pages/SellCar/components/Banner') as LoaderComponent<{ country?: string }>,
// );

interface SellCarProps {
  region: TRegionKey;
  locationCentreOptions: ILocationCentreOption[];
  locationCentres: IInspectionCentre[];
}

const StyledLeftBanner = styled.div``;

const StyledDivider = styled.div`
  border-top: 1px solid #d9d9d9;
`;

// Todo: Might need to think better way
// use region from getServerProps instead of using useRegionConfig
// because bro @huy got issue when get country in HeadMeta. Its undefined at first and HeadMeta doesn't rerender
// so we updated countryCode or country to be region from server side
const SellCar = ({ region: countryCode, locationCentreOptions, locationCentres }: SellCarProps) => {
  const { i18n } = useTranslation('sellCar');
  const [isFromMobileApp, setIsFromMobileApp] = useState<boolean>(false);
  const [width] = useWindowResize();

  // save UTM referrer to cookie
  useUTM();

  useEffect(() => {
    const isFromMobileAppCookie = jsCookie.get(CARRO_MOBILE_APP_KEY);
    setIsFromMobileApp(isFromMobileAppCookie === 'true');
  }, []);

  const isJP = ['jp'].includes(countryCode);
  const isMY = countryCode === 'my';

  const sellStepTransPathByCountry = i18n.exists(`sellCar:sellCar.sellSteps${countryCode.toUpperCase()}`)
    ? countryCode.toUpperCase()
    : '';

  const isShowFeaturedIn = !isMY && !isJP;
  const isStepsToSellCarV2 = isJP;
  const isShowWhyChooseCarro = isJP;
  const isShowUPS = isJP;
  const isCustomerReviewV2 = isJP;
  const isShowVisitUs = isJP;
  const isShowStats = !isMY && !isJP;

  // TODO: @cris
  // remove spin to resolve CXBF-1897
  // need to resolve CCV-702 again

  return (
    <Layout
      bannerContent={<SellBanner country={countryCode} />}
      hideHeader={isFromMobileApp}
      hideFooter={isFromMobileApp}
      hideSocialChat={false}
    >
      <HeadMeta metaKey="sell" region={countryCode as TRegionKey} />
      <StyledLeftBanner>
        {isShowFeaturedIn && <FeaturedIn isWithImageColor />}
        {isMY && <InspectionFeeWaive />}
        {isStepsToSellCarV2 ? (
          <StepsToSellCarV2 content={STEPS_TO_SELL_CAR_CONTENT_V2(countryCode.toUpperCase())} />
        ) : (
          <StepsToSellCar
            countryCode={countryCode}
            backgroundColor="transparent"
            stepContent={STEPS_TO_SELL_CAR_CONTENT(sellStepTransPathByCountry, countryCode === 'my')} // pass countryCode as sellStepTransPathByCountry does not exist in my-MS
          />
        )}
        {isShowWhyChooseCarro && (
          <>
            <StyledContainer>
              <StyledDivider />
            </StyledContainer>
            <WhyChooseCarro content={WHY_CHOOSE_CARRO_CONTENT()} />
          </>
        )}
        {isShowUPS && (
          <>
            <StyledContainer>
              <StyledDivider />
            </StyledContainer>
            <TrustReason content={UPS_CONTENT()} />
          </>
        )}
        {isCustomerReviewV2 ? (
          <CustomerReviewV2 content={CUSTOMER_REVIEW_CONTENT_V2[countryCode as TRegionKey]} />
        ) : (
          <CustomerReview content={CUSTOMER_REVIEW_CONTENT[countryCode as TRegionKey]} />
        )}
        {isShowVisitUs && <VisitUs content={VISIT_US_CONTENT} />}
        {isShowStats && <Stats />}
        {isMY && (
          <section aria-label="Inspection Point">
            <InspectionCentres
              countryCode={countryCode}
              locationOptions={locationCentreOptions}
              inspectionCentres={locationCentres}
            />
          </section>
        )}

        {isJP && (
          <StyledContainer>
            <StyledDivider />
          </StyledContainer>
        )}

        <section aria-label="Ask & FAQ">
          <AskFaq countryCode={countryCode || 'sg'} />{' '}
        </section>
      </StyledLeftBanner>
    </Layout>
  );
};

export default SellCar;
